import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Eye,
  EyeOff,
  User,
  Lock,
  Sparkles,
  Users,
  FileText,
  Shield,
  ArrowRight,
  Zap,
  Check,
} from "lucide-react";

export function ClientVisibilitySection() {
  const [viewMode, setViewMode] = useState("your-view"); // 'your-view' | 'client-view' | 'split-view'
  const [showTheater, setShowTheater] = useState(false);
  const [theaterStep, setTheaterStep] = useState("full"); // 'full' | 'filtering' | 'simple'
  const [hoveredDoc, setHoveredDoc] = useState(null); // number | null
  const [hasSeenTheater, setHasSeenTheater] = useState(false);

  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: "Tax_Return_2024.pdf",
      path: "Clients/ZIAAT/FY 25-26/Income Tax",
      clientVisible: true,
      category: "tax",
    },
    {
      id: 2,
      name: "Internal_Notes.pdf",
      path: "Clients/ZIAAT/FY 25-26/Internal",
      clientVisible: false,
      category: "audit",
    },
    {
      id: 3,
      name: "Invoice_Q4.pdf",
      path: "Clients/ZIAAT/FY 25-26/Invoices",
      clientVisible: true,
      category: "financial",
    },
    {
      id: 4,
      name: "Strategy_Draft.pdf",
      path: "Clients/ZIAAT/FY 25-26/Internal",
      clientVisible: false,
      category: "financial",
    },
    {
      id: 5,
      name: "Audit_Report.pdf",
      path: "Clients/ZIAAT/FY 25-26/Audit",
      clientVisible: true,
      category: "audit",
    },
    {
      id: 6,
      name: "Partner_Discussion.pdf",
      path: "Clients/ZIAAT/FY 25-26/Internal",
      clientVisible: false,
      category: "audit",
    },
    {
      id: 7,
      name: "GST_Returns.pdf",
      path: "Clients/ZIAAT/FY 25-26/GST",
      clientVisible: true,
      category: "tax",
    },
    {
      id: 8,
      name: "Compensation_Plan.pdf",
      path: "Clients/ZIAAT/FY 25-26/Internal",
      clientVisible: false,
      category: "financial",
    },
  ]);

  const toggleVisibility = (docId) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === docId ? { ...doc, clientVisible: !doc.clientVisible } : doc,
      ),
    );
  };

  const triggerTheaterReveal = () => {
    // console.log("Theater reveal triggered!"); // Debug log
    setHasSeenTheater(true);
    setShowTheater(true);
    setTheaterStep("full");

    // Step 1: Show all documents (2s)
    setTimeout(() => setTheaterStep("filtering"), 2000);

    // Step 2: Filter animation (4s total)
    setTimeout(() => setTheaterStep("simple"), 4000);

    // Step 3: Close (7s total)
    setTimeout(() => {
      setShowTheater(false);
      setViewMode("client-view");
    }, 7000);
  };

  const handleViewModeChange = (mode) => {
    // If clicking Client View for the first time, show theater animation
    if (mode === "client-view" && !hasSeenTheater) {
      triggerTheaterReveal();
    } else {
      setViewMode(mode);
    }
  };

  const clientVisibleDocs = documents.filter((d) => d.clientVisible);
  const privateDocsCount = documents.filter((d) => !d.clientVisible).length;

  const getCategoryColor = (category) => {
    switch (category) {
      case "tax":
        return "cyan";
      case "audit":
        return "gray";
      case "financial":
        return "slate";
      default:
        return "gray";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "tax":
        return FileText;
      case "audit":
        return Shield;
      case "financial":
        return Users;
      default:
        return FileText;
    }
  };

  return (
    // bg-gradient-to-br from-gray-900 via-black to-gray-800
    <div className="relative bg-white py-24 overflow-x-hidden">
      {/* Neon Grid Background */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.08) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />

      {/* Cyan Glow Orbs */}
      {/* <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[100px]" /> */}
      {/* <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-cyan-400/20 rounded-full blur-[80px]" /> */}

      {/* Theater Mode - Sequential Reveal */}
      <AnimatePresence>
        {showTheater && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl"
          >
            <div className="max-w-6xl w-full px-8">
              {theaterStep === "full" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  className="text-center"
                >
                  <p className="text-gray-400 text-sm mb-6 uppercase tracking-wider">
                    Your Complete Document Library
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {documents.map((doc, idx) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex items-center gap-3 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-4"
                      >
                        <FileText className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-200 text-sm flex-1 text-left">
                          {doc.name}
                        </span>
                        {doc.clientVisible ? (
                          <Eye className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <Lock className="w-4 h-4 text-gray-600" />
                        )}
                      </motion.div>
                    ))}
                  </div>

                  <p className="text-2xl text-gray-100">
                    Everything you've organized...
                  </p>
                </motion.div>
              )}

              {theaterStep === "filtering" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center"
                >
                  <p className="text-gray-400 text-sm mb-6 uppercase tracking-wider">
                    Filtering For Client View...
                  </p>

                  {/* Particle effect - documents flying away */}
                  <div className="relative h-96 mb-8">
                    {documents.map((doc, idx) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                        animate={
                          doc.clientVisible
                            ? { opacity: 1, x: 0, y: 0, scale: 1 }
                            : {
                                opacity: 0,
                                x: idx % 2 === 0 ? -300 : 300,
                                y: -200 + idx * 20,
                                scale: 0.3,
                                rotate: idx % 2 === 0 ? -45 : 45,
                              }
                        }
                        transition={{ duration: 1, delay: idx * 0.1 }}
                        className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
                          doc.clientVisible ? "z-10" : "z-0"
                        }`}
                        style={{ marginTop: `${idx * 8}px` }}
                      >
                        <div
                          className={`flex items-center gap-3 rounded-xl p-4 ${
                            doc.clientVisible
                              ? "bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 border border-cyan-500/30"
                              : "bg-gray-800 border border-gray-700"
                          }`}
                        >
                          <FileText
                            className={`w-5 h-5 ${
                              doc.clientVisible
                                ? "text-cyan-400"
                                : "text-gray-600"
                            }`}
                          />
                          <span
                            className={`text-sm ${
                              doc.clientVisible
                                ? "text-cyan-200"
                                : "text-gray-500"
                            }`}
                          >
                            {doc.name}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <p className="text-2xl text-gray-100">
                    Private documents fade away...
                  </p>
                </motion.div>
              )}

              {theaterStep === "simple" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ y: 20 }}
                    animate={{ y: 0 }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 border border-cyan-500/30 px-6 py-3 rounded-full mb-8"
                  >
                    <Eye className="w-5 h-5 text-cyan-400" />
                    <span className="text-cyan-300">Client View</span>
                  </motion.div>

                  <div className="space-y-3 mb-8 max-w-2xl mx-auto">
                    {clientVisibleDocs.map((doc, idx) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: idx * 0.15,
                          type: "spring",
                          stiffness: 200,
                        }}
                        className="flex items-center gap-4 bg-gradient-to-r from-gray-800 to-gray-900 border border-cyan-500/30 rounded-xl p-5"
                      >
                        <Sparkles className="w-5 h-5 text-cyan-400" />
                        <span className="text-cyan-100 flex-1 text-left text-lg">
                          {doc.name}
                        </span>
                        <Check className="w-5 h-5 text-cyan-400" />
                      </motion.div>
                    ))}
                  </div>

                  <p className="text-3xl text-gray-100 mb-3">
                    This is what they see.
                  </p>
                  <p className="text-xl text-gray-400">
                    Clean. Curated. Professional.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      {/* <div className="text-center mb-20 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <div className="inline-flex items-center gap-2 bg-cyan-500/20 border-2 border-cyan-400/50 px-5 py-2 rounded-full mb-8 shadow-[0_0_20px_rgba(6,182,212,0.3)]">
                        <Eye className="w-4 h-4 text-cyan-300" />
                        <span className="text-sm text-cyan-200">
                            Client Visibility Controls
                        </span>
                    </div>

                    <h2 className="text-6xl text-white mb-6 drop-shadow-[0_0_30px_rgba(6,182,212,0.3)]">
                        Show Them Only
                        <br />
                        <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-cyan-300 bg-clip-text text-transparent">
                            What They Need.
                        </span>
                    </h2>

                    <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
                        Control exactly what clients see. Keep internal documents private.
                        Share progress transparently.
                    </p>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="inline-flex items-center gap-2 bg-gray-800/60 border-2 border-gray-700/50 rounded-full p-1.5 shadow-lg backdrop-blur-sm"
                    >
                        {[
                            { id: "your-view", label: "Your View", icon: User },
                            { id: "split-view", label: "Compare", icon: ArrowRight },
                            { id: "client-view", label: "Client View", icon: Eye },
                        ].map((mode) => (
                            <motion.button
                                key={mode.id}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleViewModeChange(mode.id)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${viewMode === mode.id
                                    ? "bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)]"
                                    : "text-gray-400 hover:text-gray-200"
                                    }`}
                            >
                                <mode.icon className="w-4 h-4" />
                                <span className="text-sm font-medium">{mode.label}</span>
                            </motion.button>
                        ))}
                    </motion.div>
                </motion.div>
            </div> */}
      <div className="ClientVisibilitySection_">
        <div className="Client_Visibility_Content">
          {/* <div className="Client_Visibility_Badge">
            <Eye className="Client_Visibility_Badge_Icon" />
            <span className="Client_Visibility_Badge_Text">
              Client Visibility Controls
            </span>
          </div> */}

          <h2 className="Client_Visibility_Heading">
            Show Them Only <br />
            <span>What They Need.</span>
          </h2>

          <p className="Client_Visibility_Para">
            Control exactly what clients see. Keep internal documents private.
            Share progress transparently.
          </p>

          <div className="">
            {" "}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="Client_Visibility_btn"
              //   inline-flex items-center gap-2 bg-gray-800/60 border-2 border-gray-700/50 rounded-full p-1.5 shadow-lg backdrop-blur-sm
            >
              {[
                { id: "your-view", label: "Your View", icon: User },
                { id: "split-view", label: "Compare", icon: ArrowRight },
                { id: "client-view", label: "Client View", icon: Eye },
              ].map((mode) => (
                <motion.button
                  key={mode.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleViewModeChange(mode.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                    viewMode === mode.id
                      ? // bg-gradient-to-r from-cyan-400 to-cyan-500 text-white
                        "bg-white text-[#093a5b] shadow-[0_0_20px_rgba(6,182,212,0.4)]"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                >
                  <mode.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{mode.label}</span>
                </motion.button>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
      {/* Main View Container */}
      <div className="max-w-7xl mx-auto p-4">
        <AnimatePresence mode="wait">
          {/* Your View - Full Control */}
          {viewMode === "your-view" && (
            <motion.div
              key="your-view"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-3xl border border-gray-200 shadow-2xl overflow-hidden"
            >
              {/* Header */}
              {/* bg-gradient-to-br from-gray-50 to-white border-b border-gray-200 p-8 */}
              <div className="ClientVisibilitySection-view-cointainer-heading-class">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="ClientVisibilitySection-view-cointainer-heading">
                    Your Complete Library
                  </h3>

                  <div className="clientVisibleDocs-class">
                    <div className="text-right">
                      <p className="hidden md:block text_gray_500_sm">
                        Client Visible
                      </p>
                      <p className="md:hidden  text_gray_500_sm">Client</p>
                      <p className="text_cyan_600_xl">
                        {clientVisibleDocs.length}
                      </p>
                    </div>

                    <div className="hidden md:block w-px h-12 bg-gray-200" />

                    <div className="text-right">
                      <p className="text_gray_500_sm">Private</p>
                      <p className="text_gray_400_xl">{privateDocsCount}</p>
                    </div>
                  </div>
                </div>

                <p className="ClientVisibilitySection_para">
                  Toggle visibility to control what clients see
                </p>
              </div>

              {/* Documents Grid */}
              <div className="ClientVisibilitySection_padding_class">
                <div className="space-y-3">
                  {documents.map((doc, idx) => {
                    const CategoryIcon = getCategoryIcon(doc.category);
                    const color = getCategoryColor(doc.category); // kept as-is (even if unused)

                    return (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        onHoverStart={() => setHoveredDoc(doc.id)}
                        onHoverEnd={() => setHoveredDoc(null)}
                        whileHover={{ x: 4 }}
                        // relative flex items-center gap-4 p-5 rounded-xl border-2 transition-all
                        className={`ClientVisibilitySection_doctab ${
                          doc.clientVisible
                            ? // bg-gradient-to-r from-cyan-50 to-white border-cyan-200
                              "bg-[#093a5b]/80"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <CategoryIcon
                          className={`w-6 h-6 ${
                            doc.clientVisible ? "text-white" : "text-gray-400"
                          }`}
                        />

                        <div className="flex-1">
                          <p
                            className={`mb-1 doc_text ${
                              doc.clientVisible ? "text-white" : "text-gray-600"
                            }`}
                          >
                            {doc.name}
                          </p>
                          <p
                            className={`mb-1 doc_path_text ${
                              doc.clientVisible ? "text-white" : "text-gray-600"
                            }`}
                          >
                            {doc.path}
                          </p>
                        </div>

                        {/* Visibility Badge */}
                        <div
                          className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                            doc.clientVisible
                              ? "bg-cyan-100 text-cyan-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {doc.clientVisible ? (
                            <>
                              <Eye className="w-3 h-3" />
                              <span>Visible to Client</span>
                            </>
                          ) : (
                            <>
                              <Lock className="w-3 h-3" />
                              <span>Private</span>
                            </>
                          )}
                        </div>

                        {/* Toggle Button */}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => toggleVisibility(doc.id)}
                          className={`p-3 rounded-lg transition-all ${
                            doc.clientVisible
                              ? "bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg shadow-cyan-500/25"
                              : "bg-gray-200 hover:bg-gray-300 text-gray-600"
                          }`}
                        >
                          {doc.clientVisible ? (
                            <Eye className="w-5 h-5" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </motion.button>

                        {/* Hover Effect */}
                        {hoveredDoc === doc.id && (
                          <motion.div
                            layoutId="doc-glow"
                            className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-transparent rounded-xl pointer-events-none"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* Split View - Side by Side Comparison */}
          {viewMode === "split-view" && (
            <motion.div
              key="split-view"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4 }}
              className="grid md:grid-cols-2 gap-8"
            >
              {/* Your View Side */}
              <div className="bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 to-white border-b border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-gray-600" />
                    <h4 className="text-xl text-gray-900">Your View</h4>
                  </div>
                  <p className="text-sm text-gray-500">
                    Complete access to all documents
                  </p>
                </div>

                <div className="p-6 space-y-2 max-h-[600px] overflow-y-auto">
                  {documents.map((doc, idx) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        doc.clientVisible ? "bg-gray-50" : "bg-gray-100"
                      }`}
                    >
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700 flex-1">
                        {doc.name}
                      </span>
                      {doc.clientVisible ? (
                        <Eye className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Client View Side */}
              <div className="bg-gradient-to-b from-gray-900 to-black rounded-3xl border border-gray-800 shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-b border-gray-700 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Eye className="w-5 h-5 text-emerald-400" />
                    <h4 className="text-xl text-white">Client View</h4>
                  </div>
                  <p className="text-sm text-gray-400">
                    Only shared documents visible
                  </p>
                </div>

                <div className="p-6 space-y-2 max-h-[600px] overflow-y-auto">
                  {clientVisibleDocs.length > 0 ? (
                    clientVisibleDocs.map((doc, idx) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-cyan-500/20 hover:bg-white/10 transition-colors"
                      >
                        <FileText className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm text-cyan-100 flex-1">
                          {doc.name}
                        </span>
                        <Check className="w-4 h-4 text-cyan-400" />
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <EyeOff className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                      <p className="text-gray-500">No documents shared yet</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Client View Only */}
          {viewMode === "client-view" && (
            <motion.div
              key="client-view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="bg-gradient-to-b from-gray-900 to-black rounded-3xl border border-gray-800 shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-b border-gray-700 p-8">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Eye className="w-6 h-6 text-cyan-400" />
                    <h3 className="text-2xl text-white">Client Portal View</h3>
                  </div>

                  <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 rounded-lg">
                    <Sparkles className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-cyan-300">
                      {clientVisibleDocs.length} Documents
                    </span>
                  </div>
                </div>

                <p className="text-gray-400">
                  Clean, professional view for your clients
                </p>
              </div>

              {/* Documents */}
              <div className="p-8">
                {clientVisibleDocs.length > 0 ? (
                  <div className="space-y-3">
                    {clientVisibleDocs.map((doc, idx) => {
                      const CategoryIcon = getCategoryIcon(doc.category);

                      return (
                        <motion.div
                          key={doc.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.08 }}
                          whileHover={{ scale: 1.02, x: 8 }}
                          className="flex items-center gap-4 p-6 rounded-xl bg-gradient-to-r from-white/5 to-white/[0.02] border border-cyan-500/20 hover:border-cyan-500/40 transition-all cursor-pointer group"
                        >
                          <motion.div
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                          >
                            <CategoryIcon className="w-6 h-6 text-cyan-400" />
                          </motion.div>

                          <div className="flex-1">
                            <p className="text-cyan-100 mb-1 group-hover:text-white transition-colors">
                              {doc.name}
                            </p>
                            <p className="text-xs text-gray-500">{doc.path}</p>
                          </div>

                          <motion.div
                            whileHover={{ scale: 1.2 }}
                            className="p-2 bg-cyan-500/10 rounded-lg"
                          >
                            <Check className="w-5 h-5 text-cyan-400" />
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <EyeOff className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                    </motion.div>

                    <p className="text-xl text-gray-500 mb-2">
                      No documents shared
                    </p>
                    <p className="text-gray-600">
                      Toggle visibility in Your View to share documents
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Feature Highlights */}
      <div className="mt-20 grid md:grid-cols-4 gap-6 ClientVisibilitySection_padding_class">
        {[
          {
            icon: Eye,
            label: "Granular Control",
            desc: "Toggle visibility per document",
          },
          {
            icon: Shield,
            label: "Privacy First",
            desc: "Keep sensitive data private",
          },
          {
            icon: Zap,
            label: "Instant Updates",
            desc: "Changes reflect immediately",
          },
          {
            icon: Users,
            label: "Client Trust",
            desc: "Transparent collaboration",
          },
        ].map((feature, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            whileHover={{ y: -4 }}
            className="Client_visibility"
          >
            <motion.div
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.6 }}
            >
              <feature.icon className="w-8 h-8 text-[#093a5b] mx-auto mb-3 group-hover:text-cyan-500 transition-colors" />
            </motion.div>
            <p className="text-gray-900 mb-1">{feature.label}</p>
            <p className="text-sm text-gray-500">{feature.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
